package com.eladfinish.bible.contest.screens.search.history

import com.eladfinish.bible.contest.constants.Constants
import com.eladfinish.bible.contest.currentMilliseconds
import com.eladfinish.bible.contest.screens.search.model.SearchMode
import com.eladfinish.bible.contest.screens.search.model.SearchPlace
import com.russhwolf.settings.Settings
import io.github.aakira.napier.Napier
import kotlinx.coroutines.sync.Mutex
import kotlinx.coroutines.sync.withLock
import kotlinx.serialization.Serializable
import kotlinx.serialization.SerializationException
import kotlinx.serialization.json.Json

/**
 * Keeps the whole history as one versioned JSON document under a single [Settings] key. Every
 * read-modify-write runs under [mutex] and ends in a single put, so concurrent searches can't
 * lose each other's updates.
 */
class SettingsSearchHistoryRepository(
    private val settings: Settings,
    private val clock: SearchHistoryClock = SearchHistoryClock { currentMilliseconds },
) : SearchHistoryRepository {

    private val mutex = Mutex()

    private val json = Json {
        ignoreUnknownKeys = true
    }

    override suspend fun record(key: SearchHistoryKey): SearchHistoryEntry =
        mutex.withLock {
            val normalizedKey = key.normalized()
            val now = clock.nowEpochMillis()
            val entries = readEntries()

            val updated = entries[normalizedKey]
                ?.let { existing ->
                    existing.copy(
                        searchCount = existing.searchCount + 1,
                        lastSearchedAtMillis = now,
                    )
                }
                ?: SearchHistoryEntry(
                    key = normalizedKey,
                    searchCount = 1,
                    firstSearchedAtMillis = now,
                    lastSearchedAtMillis = now,
                )

            entries[normalizedKey] = updated
            writeEntries(entries.values)
            updated
        }

    override suspend fun getHistory(sort: SearchHistorySort): List<SearchHistoryEntry> =
        mutex.withLock {
            readEntries().values.sortedWith(sort.comparator)
        }

    override suspend fun delete(key: SearchHistoryKey) {
        mutex.withLock {
            val entries = readEntries()
            if (entries.remove(key.normalized()) != null) {
                writeEntries(entries.values)
            }
        }
    }

    override suspend fun clear() {
        mutex.withLock {
            settings.remove(Constants.PREF_SEARCH_HISTORY)
        }
    }

    private val SearchHistorySort.comparator: Comparator<SearchHistoryEntry>
        get() = when (this) {
            SearchHistorySort.RECENT ->
                compareByDescending { it.lastSearchedAtMillis }

            SearchHistorySort.MOST_SEARCHED ->
                compareByDescending<SearchHistoryEntry> { it.searchCount }
                    .thenByDescending { it.lastSearchedAtMillis }
        }

    private fun readEntries(): LinkedHashMap<SearchHistoryKey, SearchHistoryEntry> {
        val raw = settings.getStringOrNull(Constants.PREF_SEARCH_HISTORY)
        val document = if (raw.isNullOrEmpty()) {
            HistoryDocument()
        } else {
            try {
                json.decodeFromString(HistoryDocument.serializer(), raw)
            } catch (e: SerializationException) {
                Napier.e(message = "Unreadable search history, starting empty", throwable = e, tag = TAG)
                HistoryDocument()
            } catch (e: IllegalArgumentException) {
                Napier.e(message = "Unreadable search history, starting empty", throwable = e, tag = TAG)
                HistoryDocument()
            }
        }

        val entries = LinkedHashMap<SearchHistoryKey, SearchHistoryEntry>()
        document.entries
            .mapNotNull { it.toEntryOrNull() }
            .forEach { entries[it.key] = it }
        return entries
    }

    private fun writeEntries(entries: Collection<SearchHistoryEntry>) {
        val document = HistoryDocument(
            version = DOCUMENT_VERSION,
            entries = entries.map { it.toDto() },
        )
        settings.putString(
            key = Constants.PREF_SEARCH_HISTORY,
            value = json.encodeToString(HistoryDocument.serializer(), document),
        )
    }

    @Serializable
    private data class HistoryDocument(
        val version: Int = DOCUMENT_VERSION,
        val entries: List<EntryDto> = emptyList(),
    )

    @Serializable
    private data class EntryDto(
        val query: String,
        val onlyQri: Boolean,
        val withNikud: Boolean,
        val withTeamim: Boolean,
        val searchMode: String,
        val searchPlace: String,
        val searchCount: Int,
        val firstSearchedAt: Long,
        val lastSearchedAt: Long,
    )

    private fun SearchHistoryEntry.toDto(): EntryDto =
        EntryDto(
            query = key.query,
            onlyQri = key.onlyQri,
            withNikud = key.withNikud,
            withTeamim = key.withTeamim,
            searchMode = key.searchMode.name,
            searchPlace = key.searchPlace.name,
            searchCount = searchCount,
            firstSearchedAt = firstSearchedAtMillis,
            lastSearchedAt = lastSearchedAtMillis,
        )

    // An entry written by a version whose enum value no longer exists is dropped, not fatal.
    private fun EntryDto.toEntryOrNull(): SearchHistoryEntry? {
        val mode = SearchMode.entries.firstOrNull { it.name == searchMode } ?: return null
        val place = SearchPlace.entries.firstOrNull { it.name == searchPlace } ?: return null

        return SearchHistoryEntry(
            key = SearchHistoryKey(
                query = query,
                onlyQri = onlyQri,
                withNikud = withNikud,
                withTeamim = withTeamim,
                searchMode = mode,
                searchPlace = place,
            ),
            searchCount = searchCount,
            firstSearchedAtMillis = firstSearchedAt,
            lastSearchedAtMillis = lastSearchedAt,
        )
    }

    private companion object {
        const val TAG = "SearchHistory"
        const val DOCUMENT_VERSION = 1
    }
}
